// ⚠️ DEPRECATED — Etappe 4.4 (2026-06-27): Verwendet nur noch durch PlatserAdmin.js,
// das selbst deprecated ist. Wird mit dem Marker-fixer-Cleanup entfernt.
//
// FlyttaMarkorDialog – modal för att manuellt placera/flytta en markör
// för foretag / foreningar / karttipps / vardagskort / tjanstekort.
//
// Användning:
//   const [open, setOpen] = useState(false);
//   <button onClick={() => setOpen(true)}>📍 Flytta markör</button>
//   {open && (
//     <FlyttaMarkorDialog
//       kind="foretag"           // ALLOWED_COORD_COLLECTIONS
//       itemId={f.id}
//       itemName={f.foretagsnamn}
//       lat={f.lat}              // valfritt – startposition
//       lng={f.lng}
//       onClose={() => setOpen(false)}
//       onSaved={({lat,lng}) => { /* refresh local data */ }}
//     />
//   )}
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, Save, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default-center: Backe by
const DEFAULT_CENTER = { lat: 63.81667, lng: 16.4 };

function authHeaders() {
  const token = sessionStorage.getItem("mod_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function FlyttaMarkorDialog({ kind, itemId, itemName, lat, lng, onClose, onSaved }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markerRef = useRef(null);
  const [pos, setPos] = useState({
    lat: typeof lat === "number" ? lat : DEFAULT_CENTER.lat,
    lng: typeof lng === "number" ? lng : DEFAULT_CENTER.lng,
  });
  const [hadInitialCoord, setHadInitialCoord] = useState(typeof lat === "number" && typeof lng === "number");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Initiera Leaflet en gång.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const L = (await import("leaflet")).default;
      if (cancelled || !mapRef.current) return;
      // Tile-providerstring som matchar resten av sajten (OSM)
      mapInstance.current = L.map(mapRef.current, {
        center: [pos.lat, pos.lng],
        zoom: hadInitialCoord ? 16 : 12,
        scrollWheelZoom: true,
      });
      L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; OpenStreetMap-bidragsgivare',
        maxZoom: 19,
      }).addTo(mapInstance.current);

      // Dragable marker
      markerRef.current = L.marker([pos.lat, pos.lng], { draggable: true }).addTo(mapInstance.current);
      markerRef.current.on("dragend", (e) => {
        const { lat: la, lng: ln } = e.target.getLatLng();
        setPos({ lat: la, lng: ln });
      });

      // Click på karta = flytta markör dit
      mapInstance.current.on("click", (e) => {
        const { lat: la, lng: ln } = e.latlng;
        markerRef.current.setLatLng([la, ln]);
        setPos({ lat: la, lng: ln });
      });
    })();
    return () => {
      cancelled = true;
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, []);

  const save = async () => {
    setError("");
    setSaving(true);
    try {
      const res = await axios.patch(
        `${API}/mod/coords/${kind}/${itemId}`,
        { lat: pos.lat, lng: pos.lng },
        { headers: authHeaders() }
      );
      onSaved && onSaved({ lat: res.data.lat, lng: res.data.lng });
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Kunde inte spara");
    }
    setSaving(false);
  };

  return (
    <div
      data-testid="flytta-markor-dialog"
      className="fixed inset-0 z-[1000] bg-ink/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-parchment border border-warm-border rounded-sm max-w-2xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-warm-border flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-body font-semibold text-bark/60 uppercase tracking-[0.2em]">Flytta markör</p>
            <h3 className="font-heading text-lg font-semibold text-ink mt-0.5 truncate">{itemName || "(ingen titel)"}</h3>
            <p className="text-xs text-bark/60 mt-1">Klicka på kartan eller dra markören till rätt plats.</p>
          </div>
          <button
            data-testid="flytta-markor-close"
            onClick={onClose}
            className="text-bark hover:text-falu p-1 -mr-1 shrink-0"
            aria-label="Stäng"
          >
            <X size={20} />
          </button>
        </div>

        <div className="relative">
          <div ref={mapRef} data-testid="flytta-markor-map" className="w-full h-[60vh] min-h-[380px] bg-bone" />
        </div>

        <div className="px-5 py-3 border-t border-warm-border bg-bone-alt flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs font-mono text-bark/80">
            <MapPin size={12} className="inline mr-1" />
            {pos.lat.toFixed(6)}, {pos.lng.toFixed(6)}
          </div>
          <div className="flex items-center gap-2">
            {error && <span className="text-xs text-falu">{error}</span>}
            <button
              data-testid="flytta-markor-save"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-1.5 bg-pine hover:bg-pine-hover disabled:opacity-50 text-parchment text-sm font-semibold px-4 py-2 rounded-sm transition-colors"
            >
              <Save size={14} /> {saving ? "Sparar…" : "Spara position"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
